// The server's view of a survey's routes, as the builder shows it.
//
// ValidateSurveyRoutes answers { valid, problems, warnings }, each entry
// { itemId, ruleId?, code, message } - itemId is the skip-logic section, ruleId
// the rule (absent for the section's otherwise route). Problems stop the survey
// being published; warnings do not. A change that would leave a published
// survey's routes broken is refused with 409 and the same body.

export const NO_ROUTE_PROBLEMS = {problems: [], warnings: []}

// The routes a validation answered, safe to read whatever came back.
export const routesFrom = response => ({
  problems: Array.isArray(response?.problems) ? response.problems : [],
  warnings: Array.isArray(response?.warnings) ? response.warnings : [],
})

// What a failed item save, delete or reorder said, for the builder to show:
// the route problems of a 409, or the server's own message (the item endpoints
// answer a refused change with plain text).
export const readItemChangeError = err => {
  if (Array.isArray(err?.problems)) {
    return {
      problems: err.problems,
      message:
        'This survey is published, so the change was not saved: it would break its skip logic. Fix the rules first.',
    }
  }
  const message = typeof err === 'string' ? err : err?.message || err?.Message
  return {problems: [], message: typeof message === 'string' && message ? message : null}
}

// One section's problems and warnings, split into those about a rule (by rule
// id) and those about the section itself (its otherwise route, or rules that
// could not be read).
export const routesForSection = (routes, sectionItemId) => {
  const mine = entry => sectionItemId != null && entry.itemId === sectionItemId
  const problems = (routes?.problems || []).filter(mine)
  const warnings = (routes?.warnings || []).filter(mine)
  return {
    forRule: ruleId => ({
      problems: problems.filter(p => p.ruleId === ruleId),
      warnings: warnings.filter(w => w.ruleId === ruleId),
    }),
    section: {
      problems: problems.filter(p => p.ruleId == null),
      warnings: warnings.filter(w => w.ruleId == null),
    },
    count: problems.length,
  }
}

// A short account of why publishing was refused, naming each section by its
// position in the builder's list.
export const describePublishRefusal = (routes, entries = []) => {
  const problems = routes?.problems || []
  if (problems.length === 0) {
    return 'The survey cannot be published until its skip logic is fixed.'
  }
  // Each problem named as the builder shows it - the section's place in the list and the rule's
  // number in it - rather than by the ids the server uses.
  const where = problem => {
    const index = entries.findIndex(e => e?.itemId === problem.itemId)
    if (index < 0) return 'A skip-logic section'
    const ruleIndex = (entries[index].rules?.rules || []).findIndex(r => r.id === problem.ruleId)
    return ruleIndex >= 0
      ? `Skip logic at position ${index + 1}, rule ${ruleIndex + 1}`
      : `Skip logic at position ${index + 1}`
  }
  const lines = problems
    .slice(0, 3)
    .map(p => `${where(p)}: ${String(p.message ?? '').replace(/^Rule '[^']*'[:]?\s*/, '')}`)
  const more = problems.length > 3 ? ` (and ${problems.length - 3} more)` : ''
  return `The survey cannot be published until its skip logic is fixed. ${lines.join(' ')}${more}`
}
